import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import "./CopilotManualModal.css";
import { generatePrompt, processAIResponse } from "./AIGenerateContent";

const CopilotManualModal = ({
  show,
  onClose,
  description,
  questionCounts,
  onGenerate,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [promptCopied, setPromptCopied] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const generatedPrompt = generatePrompt(description, questionCounts);

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      setPromptCopied(true);
      setCurrentStep(2);
      setTimeout(() => setPromptCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy prompt:", err);
      alert("Failed to copy prompt. Please copy it manually.");
    }
  };

  const handleProcessResponse = async () => {
    if (!aiResponse.trim()) {
      alert("Please paste the AI response first.");
      return;
    }

    setIsProcessing(true);
    try {
      const newMaterials = processAIResponse(aiResponse);
      onGenerate(newMaterials);
      onClose();
    } catch (error) {
      console.error("Error processing AI response:", error);
      alert(
        "Error processing the AI response. Please make sure you pasted valid JSON content."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setPromptCopied(false);
    setAiResponse("");
    setIsProcessing(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="copilot-manual-modal-overlay">
      <div className="copilot-manual-modal">
        <button className="close-button" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="modal-header">
          <div className="copilot-logo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z" />
              <path d="M12 6c-3.309 0-6 2.691-6 6s2.691 6 6 6 6-2.691 6-6-2.691-6-6-6zm0 10c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z" />
              <circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <h2>Manual AI Content Generation</h2>
        </div>

        <div className="copilot-manual-guide">
          <p className="guide-intro">
            Use your preferred AI assistant (GitHub Copilot, ChatGPT, Claude,
            etc.) to generate content manually.
          </p>
        </div>

        <div className="steps-container">
          {/* Step 1 */}
          <div className={`step-section ${currentStep >= 1 ? "active" : ""}`}>
            <div className="copilot-manual-guide">
              <p
                className={`guide-step ${
                  description.trim() ? "completed" : ""
                }`}
              >
                Step 1: Review your content description
              </p>
            </div>
            <div className="step-content">
              <label>Your Description:</label>
              <div className="description-display">
                {description || "No description provided"}
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className={`step-section ${currentStep >= 2 ? "active" : ""}`}>
            <div className="copilot-manual-guide">
              <p className={`guide-step ${promptCopied ? "completed" : ""}`}>
                Step 2: Copy the generated prompt for your AI assistant
              </p>
            </div>
            <div className="step-content">
              <div className="prompt-section">
                <label>Generated Prompt:</label>
                <textarea
                  value={generatedPrompt}
                  readOnly
                  className="prompt-textarea"
                  rows={8}
                />
                <button
                  className="copy-prompt-button"
                  onClick={handleCopyPrompt}
                  disabled={!description.trim()}
                >
                  <FontAwesomeIcon icon={promptCopied ? faCheck : faCopy} />
                  {promptCopied ? "Copied!" : "Copy Prompt"}
                </button>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className={`step-section ${currentStep >= 3 ? "active" : ""}`}>
            <div className="copilot-manual-guide">
              <p
                className={`guide-step ${aiResponse.trim() ? "completed" : ""}`}
              >
                Step 3: Paste the AI response back here
              </p>
            </div>
            <div className="step-content">
              <label>AI Response (JSON format):</label>
              <textarea
                value={aiResponse}
                onChange={(e) => {
                  setAiResponse(e.target.value);
                  if (e.target.value.trim() && currentStep < 3) {
                    setCurrentStep(3);
                  }
                }}
                placeholder="Paste the JSON response from your AI assistant here..."
                className="response-textarea"
                rows={10}
              />
              {currentStep >= 3 && aiResponse.trim() && (
                <div className="ready-indicator">
                  ✅ Ready to generate content! Click the button below.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          {/* Debug info - remove this later */}
          <div
            style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}
          >
            Debug: Step {currentStep}, Prompt copied:{" "}
            {promptCopied ? "Yes" : "No"}, Response length: {aiResponse.length}
          </div>

          {currentStep < 2 && (
            <button
              className="next-step-button"
              onClick={() => setCurrentStep(2)}
              disabled={!description.trim()}
            >
              Continue to Step 2
            </button>
          )}

          {currentStep >= 3 && (
            <button
              className="process-response-button"
              onClick={handleProcessResponse}
              disabled={!aiResponse.trim() || isProcessing}
            >
              {isProcessing ? "Processing..." : "Generate Content"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopilotManualModal;
